import React, { useState } from 'react';
import { Eye, EyeOff, Heart, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import { useNavigate, Link } from 'react-router-dom';

const Signup = () => {
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    date_of_birth: '',
    gender: '',
    language: 'english',
    agreedToTerms: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Real-time password validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length > 0 && password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (password.length > 0 && !/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (password.length > 0 && !/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (password.length > 0 && !/[0-9]/.test(password)) {
      errors.push('One number');
    }
    if (password.length > 0 && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('One special character (!@#$%^&*...)');
    }
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time password validation
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
      
      // Check confirm password match
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    }

    // Real-time confirm password validation
    if (name === 'confirmPassword') {
      if (value && value !== formData.password) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.name || !formData.email) {
        toast({
          title: "Please Complete",
          description: "Name and email are required to continue",
          variant: "destructive",
        });
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      await register(formData.name, formData.email, formData.password, {
        gender: formData.gender || null,
        date_of_birth: formData.date_of_birth || null, // Convert empty string to null
        language: formData.language
      });
      
      // Track affiliate lead signup with GHL
      if (window.affiliateManager && typeof window.affiliateManager.trackLead === 'function') {
        try {
          // Split name into first and last name
          const nameParts = formData.name.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          window.affiliateManager.trackLead(
            {
              firstName: firstName,
              lastName: lastName,
              email: formData.email
            },
            function () {
              console.log('Lead created successfully with GHL affiliate tracking');
            }
          );
        } catch (affiliateError) {
          // Don't block signup if affiliate tracking fails
          console.error('Affiliate tracking error:', affiliateError);
        }
      }
      
      toast({
        title: "🌟 Welcome to Constant Love!",
        description: "Your self-love journey begins now. Get ready to transform how you see yourself!",
        duration: 5000,
      });
      
      // Navigate to onboarding using React Router (maintains context state)
      setTimeout(() => {
        navigate('/onboarding');
      }, 1500);
    } catch (error) {
      // Better error message handling
      let errorMessage = "Something went wrong. Please try again.";
      
      if (error.message) {
        errorMessage = typeof error.message === 'string' 
          ? error.message 
          : JSON.stringify(error.message);
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast({
        title: "Signup Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center py-2 px-4">
      <Card className="w-full max-w-md p-4 bg-white/80 backdrop-blur-sm border-rose-200 shadow-xl">
        {/* Compact Header */}
        <div className="text-center mb-2">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-rose-600 mb-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex justify-center mb-1">
            <img 
              src="/constant-love-logo.png" 
              alt="Constant Love™" 
              className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
            />
          </div>
          
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            Join the Constant Love™ Movement
          </h2>
          <p className="text-gray-600 text-sm">
            Start your 7-day free trial • No credit card required
          </p>
        </div>

        {/* Free Trial Banner */}
        <div className="bg-gradient-to-r from-rose-100 to-pink-100 border-2 border-rose-300 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-gray-900 mb-1">
                🎉 Start with Growth Plan (Free for 7 Days)
              </h3>
              <p className="text-xs text-gray-700 mb-2">
                All new users get full access to our premium Growth Plan features for 7 days, including:
              </p>
              <ul className="text-xs text-gray-700 space-y-1 mb-2">
                <li>✓ AI Companion (Conchi) - Unlimited conversations</li>
                <li>✓ Love Garden Game - All levels unlocked</li>
                <li>✓ Vision Board Maker - Unlimited boards</li>
                <li>✓ Divine Guidance - Daily readings</li>
                <li>✓ Advanced Journaling & Love Vault</li>
              </ul>
              <div className="bg-green-50 border border-green-300 rounded-lg px-3 py-2 mt-3">
                <p className="text-sm font-bold text-green-700 text-center">
                  💳 No Credit Card Required to Sign Up
                </p>
              </div>
              <p className="text-xs font-semibold text-rose-700 mt-2">
                After 7 days: $17/month or switch to Free Reflect Plan
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Cancel anytime during trial - no charges if you downgrade
              </p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex space-x-2">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-rose-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    step
                  )}
                </div>
                {step < 3 && (
                  <div className={`w-8 h-1 mx-2 ${
                    step < currentStep ? 'bg-rose-500' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Let's get to know you</h3>
                <p className="text-gray-600 text-sm">Tell us a bit about yourself to personalize your experience</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 h-12"
                  placeholder="What should we call you?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 h-12"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <Button
                type="button"
                onClick={nextStep}
                disabled={!formData.name || !formData.email}
                className="w-full bg-rose-500 hover:bg-rose-600 py-3"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Password & Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Create your account</h3>
                <p className="text-gray-600 text-sm">Set up your secure access to the movement</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 h-12 ${
                    passwordErrors.length > 0
                      ? 'border-red-500 focus:ring-red-300 focus:border-red-300'
                      : 'border-rose-200 focus:ring-rose-300 focus:border-rose-300'
                  }`}
                  placeholder="Create a strong password"
                  required
                />
                {passwordErrors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs font-medium text-red-600">Password must include:</p>
                    <ul className="text-xs text-red-500 space-y-1">
                      {passwordErrors.map((error, idx) => (
                        <li key={idx}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 h-12 ${
                    confirmPasswordError
                      ? 'border-red-500 focus:ring-red-300 focus:border-red-300'
                      : 'border-rose-200 focus:ring-rose-300 focus:border-rose-300'
                  }`}
                  placeholder="Confirm your password"
                  required
                />
                {confirmPasswordError && (
                  <p className="text-red-500 text-xs mt-1">{confirmPasswordError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Language *
                </label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 h-12"
                  required
                >
                  <option value="english">🇺🇸 English</option>
                  <option value="spanish">🇪🇸 Español</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Your companion and all content will be in this language</p>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={nextStep}
                  disabled={!formData.password || !formData.confirmPassword || passwordErrors.length > 0 || confirmPasswordError}
                  className="flex-1 bg-rose-500 hover:bg-rose-600"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Final Details */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Almost there!</h3>
                <p className="text-gray-600 text-sm">Just a few more details to personalize your experience</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth (Optional)
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  min="1920-01-01"
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 h-12"
                  placeholder="YYYY-MM-DD"
                />
                <p className="text-xs text-gray-500 mt-1">Used for personalized astrology insights. Click the calendar icon to select year first.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender (Optional)
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 h-12"
                >
                  <option value="">Prefer not to say</option>
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                </select>
              </div>

              {/* GDPR Consent Checkbox */}
              <div className="pt-4 pb-2 border-t border-rose-100">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.agreedToTerms}
                    onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                    className="mt-1 w-5 h-5 text-rose-500 border-rose-300 rounded focus:ring-rose-300 cursor-pointer"
                    required
                    data-testid="terms-consent-checkbox"
                  />
                  <span className="text-sm text-gray-600 leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms-of-service" target="_blank" className="text-rose-600 hover:text-rose-700 underline font-medium">
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy-policy" target="_blank" className="text-rose-600 hover:text-rose-700 underline font-medium">
                      Privacy Policy
                    </Link>
                    . I understand my data will be processed according to GDPR regulations.
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2 ml-8">
                  ✓ You can request your data export or deletion at any time
                </p>
              </div>

              <div className="flex space-x-3">
                <Button
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !formData.agreedToTerms}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="signup-submit-btn"
                >
                  {isLoading ? 'Creating Account...' : 'Join the Movement'}
                </Button>
              </div>
            </div>
          )}
        </form>

        {/* Trust Indicators */}
        <div className="flex justify-center space-x-4 mt-4 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <CheckCircle className="w-4 h-4" />
            <span>SSL Protected</span>
          </div>
          <div className="flex items-center space-x-1">
            <CheckCircle className="w-4 h-4" />
            <span>Cancel Anytime</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Signup;